import React from 'react';
import PropTypes from 'prop-types';

/** Footer con Email di contatto + Disclaimer (stackati in fondo) */
const FooterBars = ({
  contactEmail = 'sweet-trading@support.com', // ← cambia qui o passa come prop
  contactLabel = 'Contact us:',
  riskPercent = '72.00%',
  providerLabel = 'this provider',
  infoLink = '#'
}) => (
  <div
    className="absolute bottom-0 left-0 w-full z-20"
    role="contentinfo"
    aria-label="Contact and risk disclaimer"
  >
    <div className="mx-auto max-w-5xl px-4">
      {/* Email bar (sopra) */}
      {contactEmail && (
        <div className="bg-neutral-700/90 text-gray-50 shadow-[0_-4px_18px_rgba(0,0,0,0.25)]">
          <div className="px-4 sm:px-6 py-2 sm:py-2.5 text-center text-[11px] sm:text-xs md:text-sm">
            <span className="opacity-90 mr-1">{contactLabel}</span>
            <a
              href={`mailto:${contactEmail}`}
              className="underline underline-offset-2 break-all"
              aria-label={`Email ${contactEmail}`}
            >
              {contactEmail}
            </a>
          </div>
        </div>
      )}

      {/* Disclaimer (sotto, con bordi arrotondati in alto come nello screenshot) */}
      <div className="bg-neutral-800/95 text-gray-200 rounded-t-md shadow-[0_-6px_24px_rgba(0,0,0,0.35)]">
        <div className="px-4 sm:px-6 py-3 sm:py-4 text-center">
          <h2 className="text-white font-semibold text-sm sm:text-base mb-1">Disclaimer</h2>
          <p className="text-[11px] sm:text-xs md:text-sm leading-relaxed opacity-90">
            The products offered on our website are complex derivative products that carry a
            significant risk of potential loss. CFDs are complex instruments with a high risk of
            losing money rapidly due to leverage.{' '}
            <a
              href={infoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-red-500 underline-offset-2 text-red-400"
            >
              {riskPercent} of retail investor accounts lose money when trading CFDs with {providerLabel}.
            </a>{' '}
            You should consider whether you understand how these products work and whether you can
            afford to take the high risk of losing money.
          </p>
        </div>
      </div>
    </div>
  </div>
);

FooterBars.propTypes = {
  contactEmail: PropTypes.string,
  contactLabel: PropTypes.string,
  riskPercent: PropTypes.string,
  providerLabel: PropTypes.string,
  infoLink: PropTypes.string
};

/**
 * Componente BrokerHero - Ottimizzato per mobile con enfasi su partnership
 * Include email di contatto sopra al disclaimer in fondo pagina
 */
const BrokerHero = ({
  backgroundImage = '/brokerd.jpg',
  partnerLogo = '',
  partnerName = 'Strategic Partner',
  ctaLink = 'https://deriv.com',
  ctaText = 'Join Now',
  collaborationText = 'In partnership with',
  // footer props
  showFooter = true,
  contactEmail = 'sweet-trading@support.com', // ← puoi passarlo dalla pagina che lo usa
  contactLabel = 'Contact us:',
  riskPercent = '72.00%',
  disclaimerProviderLabel = 'this provider',
  disclaimerLink = '#'
}) => {
  return (
    <section
      className="relative min-h-[90vh] md:min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center px-4 sm:px-6"
      style={{
        backgroundImage: `url('${backgroundImage}')`,
        backgroundPosition: 'center center',
        backgroundAttachment: 'fixed'
      }}
      role="banner"
      aria-label={`Trading platform with ${partnerName}`}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/80 to-black/50 md:from-black/70 md:via-black/50 md:to-black/70 backdrop-blur-[2px] md:backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Contenuto */}
      <div className="relative z-10 w-full max-w-4xl mx-auto text-center text-white px-4 py-8 md:py-12 pb-48 md:pb-52">
        {/* Partner */}
        {partnerLogo && (
          <div className="mb-6 md:mb-8 flex flex-col items-center">
            <p className="text-xs md:text-sm uppercase tracking-widest text-gray-300 mb-1 md:mb-2">
              {collaborationText}
            </p>
            <img
              src={partnerLogo}
              alt={partnerName}
              className="h-8 md:h-12 object-contain max-w-[180px] md:max-w-xs"
              loading="lazy"
              width="180"
              height="48"
            />
          </div>
        )}

        {/* Titolo */}
        <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-extrabold leading-snug md:leading-tight mb-4 md:mb-6 px-2">
          Advanced trading{' '}
          <span className="text-red-500 block md:inline">powered by {partnerName}</span>
        </h1>

        {/* Testo */}
        <div className="max-w-md mx-auto px-1">
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 md:mb-8 font-medium leading-relaxed">
            Our partnership gives you <span className="font-bold">exclusive access</span> to{' '}
            <span className="font-bold">230+ assets</span> and{' '}
            <span className="font-bold">premium conditions</span>.
          </p>
        </div>

        {/* CTA */}
        <div className="flex justify-center">
          <a
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white text-sm md:text-base font-semibold py-3 px-6 md:px-8 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 md:active:scale-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 min-w-[160px] md:min-w-[200px] touch-target"
          >
            {ctaText}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 md:h-5 md:w-5 ml-1 md:ml-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>

        {/* Cards */}
        <div className="mt-8 md:mt-12 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left min-w-[300px] sm:min-w-0">
            {[
              { title: 'Co-Developed Tools', desc: 'Solutions from our technical partnership' },
              { title: 'Shared Liquidity', desc: 'Better execution from combined pools' },
              { title: 'Dedicated Support', desc: 'Joint team for partnership clients' }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white/10 p-4 md:p-6 rounded-lg backdrop-blur-[2px] md:backdrop-blur-sm flex-shrink-0 sm:flex-shrink min-w-[85%] sm:min-w-0"
              >
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">{item.title}</h3>
                <p className="text-sm md:text-base text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mini-disclaimer interno (opzionale) */}
        <p className="mt-8 md:mt-12 text-xs md:text-sm text-gray-300 opacity-80 px-2">
          Trading involves risk. Capital at risk. Services provided under our agreement with {partnerName}.
        </p>
      </div>

      {/* Footer con Email + Disclaimer (sticky in fondo alla sezione) */}
      {showFooter && (
        <FooterBars
          contactEmail={contactEmail}
          contactLabel={contactLabel}
          riskPercent={riskPercent}
          providerLabel={disclaimerProviderLabel}
          infoLink={disclaimerLink}
        />
      )}
    </section>
  );
};

BrokerHero.propTypes = {
  backgroundImage: PropTypes.string,
  partnerLogo: PropTypes.string,
  partnerName: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaText: PropTypes.string,
  collaborationText: PropTypes.string,
  showFooter: PropTypes.bool,
  contactEmail: PropTypes.string,
  contactLabel: PropTypes.string,
  riskPercent: PropTypes.string,
  disclaimerProviderLabel: PropTypes.string,
  disclaimerLink: PropTypes.string
};

// Stili globali per migliorare l'interazione mobile (ingrandisce l'area tappabile)
const mobileOptimizationStyles = `
  .touch-target { position: relative; }
  .touch-target::after {
    content: '';
    position: absolute;
    top: -10px; left: -10px; right: -10px; bottom: -10px;
  }
`;

// Inietta gli stili globali lato client
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = mobileOptimizationStyles;
  document.head.appendChild(styleElement);
}

export default BrokerHero;


